import { z } from 'zod';
import { HqNotificationSchema } from './hq-notifications';
import { AcceptInviteSchema, AuthSessionSchema, ChangeEmailSchema, ChangePasswordSchema, ConfirmEmailSchema, DeleteAccountSchema, DeletionScheduledSchema, ForgotPasswordResponseSchema, ForgotPasswordSchema, InvitePreviewSchema, LoginSchema, MeSchema, PlatformLoginResponseSchema, PlatformLoginSchema, PlatformMeSchema, PlatformSessionSchema, PlatformSetupPreviewSchema, PlatformSetupSchema, PlatformTotpVerifySchema, ProfileUpdateSchema, RefreshResponseSchema, ResetPasswordSchema, ResetTokenStateSchema, SessionInfoSchema, TotpConfirmSchema, TotpEnrolSchema, TransferOwnershipSchema, } from './auth';
import { BroadcastCreateSchema, BroadcastSchema, FreeTierStatusSchema, HqEnterTenantResultSchema, HqOverviewSchema, HqTenantActionSchema, HqTenantCreateResultSchema, HqTenantCreateSchema, HqTenantDeleteSchema, HqTenantDetailSchema, HqTenantListQuery, HqTenantRowSchema, HqTenantUsersSchema, PlatformUserInviteResultSchema, PlatformUserInviteSchema, PlatformUserSchema, PlatformUserUpdateSchema, } from './admin';
import { BoardClearSchema, BoardCreateSchema, BoardDeleteSchema, BoardDetailSchema, BoardFrameSchema, BoardUpdateSchema, FramePatchSchema, FrameUpsertSchema, StickyBatchMoveSchema, StickyBoardSchema, StickyConvertSchema, StickyCreateSchema, StickyPatchSchema, StickySchema, } from './boards';
import { Id, OkSchema, PageQuerySchema, SearchQuery, Slug, UserRefSchema, pageOf } from './common';
import { None, ep } from './endpoint-def';
import { MuteMissionSchema, NotificationCountsSchema, NotificationIdsSchema, NotificationListQuery, NotificationPreferencesSchema, NotificationPreferencesUpdateSchema, NotificationSchema, PushConfigSchema, PushDeviceSchema, PushSubscribeSchema, ReadAllResultSchema, } from './notifications';
import { OfferClaimSchema, OfferLinkCreateSchema, OfferLinkSchema, OfferLinkUpdateSchema, OfferShareSchema, PublicOfferSchema, SlugAvailabilitySchema, } from './offers';
import { BillingSummarySchema, EffectiveEntitlementSchema, EntitlementOverrideSetSchema, EntitlementScopeQuery, PlanCreateSchema, PlanInquiryCreateSchema, PlanInquirySchema, PlanInquiryUpdateSchema, PlanPublishSchema, PlanReorderSchema, PlanSchema, PlanUpdateSchema, PlanVersionSchema, SubscriptionAssignSchema, SubscriptionExtendSchema, SubscriptionSchema, } from './plans';
import { PermissionMatrixSchema, RoleCreateSchema, RoleSchema, RoleUpdateSchema } from './rbac';
import { AuditEntrySchema, AuditQuery, HealthSchema, JobRunSchema, JobSchema, JobTickResultSchema, PresignRequestSchema, PresignResponseSchema, ReleaseCreateSchema, ReleaseSchema, UploadCompleteSchema, VersionSchema, } from './system';
import { BrandingSchema, BrandingUpdateSchema, CompanyDeleteSchema, DataExportSchema, DomainAddSchema, DomainInstructionsEmailSchema, TenantDomainSchema, TenantPublicConfigSchema, TenantSchema, TenantUpdateSchema, } from './tenants';
import { InvitationSchema, InviteCreateResultSchema, InviteCreateSchema, MemberListQuery, MemberRemoveSchema, MemberSchema, MemberUpdateSchema, } from './users';
import { AttachmentCreateSchema, AttachmentSchema, ChecklistCreateSchema, ChecklistItemSchema, ChecklistUpdateSchema, ClientSchema, ClientUpsertSchema, CommentCreateSchema, CommentSchema, CommentUpdateSchema, CustomFieldCreateSchema, CustomFieldDeleteSchema, CustomFieldSchema, CustomFieldUpdateSchema, CustomValue, FolderCreateSchema, FolderSchema, FolderUpdateSchema, MissionBulkResultSchema, MissionBulkSchema, MissionControlSchema, MissionCreateSchema, MissionDetailSchema, MissionGroupsSchema, MissionListQuery, MissionMoveSchema, MissionSummarySchema, MissionUpdateSchema, MyMissionsCountsSchema, MyMissionsQuery, ProjectCreateSchema, ProjectDeleteSchema, ProjectListQuery, ProjectMembersSchema, ProjectSchema, ProjectUpdateSchema, ReactionSchema, SearchResultSchema, StatusCreateSchema, StatusDeleteSchema, StatusSchema, StatusUpdateSchema, TagCreateSchema, TagSchema, TimelineItemSchema, TimelineQuery, UndoSchema, ViewCreateSchema, ViewSchema, ViewUpdateSchema, WorkspaceCreateSchema, WorkspaceDeleteSchema, WorkspaceSchema, WorkspaceUpdateSchema, } from './work';
/* ============================================================================
   ENDPOINT MANIFEST: every route both React apps use. Keys are stable and
   referenced by @missions/api-client (`api.call('missions.get', { params })`)
   and by @missions/mocks. Owners: see docs/CONTRACTS.md.
   ========================================================================== */
const P = {
    id: z.object({ id: Id }),
    token: z.object({ token: z.string().min(20) }),
    slug: z.object({ slug: z.string().min(1) }),
    projectId: z.object({ projectId: Id }),
    missionField: z.object({ id: Id, fieldId: Id }),
    commentEmoji: z.object({ id: Id, emoji: z.string() }),
    tenantDomain: z.object({ id: Id, domainId: Id }),
    tenantOverride: z.object({ id: Id, overrideId: Id }),
    jobName: z.object({ name: z.string() }),
};
const list = (item) => z.object({ data: z.array(item) });
const UndoResult = z.object({ ok: z.literal(true), undoToken: z.string() });
const CountResult = z.object({ count: z.number().int().nonnegative() });
const SetupUrlResult = z.object({ setupUrl: z.string().url() });
const StreamResponse = z.string();
export const endpoints = {
    /* ===================== System (API-B) ===================== */
    'system.health': ep({
        method: 'GET',
        path: '/v1/health',
        summary: 'Liveness + DB check',
        owner: 'API-B',
        auth: 'public',
        response: HealthSchema,
    }),
    'system.version': ep({
        method: 'GET',
        path: '/v1/version',
        summary: 'Current API build (apps poll /version.json of their own build)',
        owner: 'API-B',
        auth: 'public',
        response: VersionSchema,
    }),
    'system.jobsTick': ep({
        method: 'POST',
        path: '/internal/jobs/tick',
        summary: 'Run due background jobs (GitHub Actions every 10 min)',
        owner: 'API-B',
        auth: 'jobs',
        response: JobTickResultSchema,
    }),
    /* ===================== Auth & account (API-A) ===================== */
    'auth.login': ep({
        method: 'POST',
        path: '/v1/auth/login',
        summary: 'Sign in; sets refresh cookie',
        owner: 'API-A',
        auth: 'public',
        body: LoginSchema,
        response: AuthSessionSchema,
    }),
    'auth.refresh': ep({
        method: 'POST',
        path: '/v1/auth/refresh',
        summary: 'Rotate refresh cookie → new access token',
        owner: 'API-A',
        auth: 'public',
        response: RefreshResponseSchema,
    }),
    'auth.logout': ep({
        method: 'POST',
        path: '/v1/auth/logout',
        summary: 'Revoke current session',
        owner: 'API-A',
        auth: 'tenant',
        response: OkSchema,
    }),
    'auth.forgotPassword': ep({
        method: 'POST',
        path: '/v1/auth/forgot-password',
        summary: 'Always 200 (no enumeration); emails a 30-min link',
        owner: 'API-A',
        auth: 'public',
        body: ForgotPasswordSchema,
        response: ForgotPasswordResponseSchema,
    }),
    'auth.resetCheck': ep({
        method: 'GET',
        path: '/v1/auth/reset-password/:token',
        summary: 'Is the reset link valid/expired/used?',
        owner: 'API-A',
        auth: 'public',
        params: P.token,
        response: ResetTokenStateSchema,
    }),
    'auth.resetPassword': ep({
        method: 'POST',
        path: '/v1/auth/reset-password',
        summary: 'Set new password; revokes all sessions',
        owner: 'API-A',
        auth: 'public',
        body: ResetPasswordSchema,
        response: OkSchema,
    }),
    'auth.invitePreview': ep({
        method: 'GET',
        path: '/v1/auth/invitations/:token',
        summary: 'Invitation details for the accept screen',
        owner: 'API-A',
        auth: 'public',
        params: P.token,
        response: InvitePreviewSchema,
    }),
    'auth.acceptInvite': ep({
        method: 'POST',
        path: '/v1/auth/invitations/accept',
        summary: 'Accept invite: set name + password, signs in',
        owner: 'API-A',
        auth: 'public',
        body: AcceptInviteSchema,
        response: AuthSessionSchema,
    }),
    'auth.confirmEmail': ep({
        method: 'POST',
        path: '/v1/auth/confirm-email',
        summary: 'Confirm a pending email change',
        owner: 'API-A',
        auth: 'public',
        body: ConfirmEmailSchema,
        response: OkSchema,
    }),
    'me.get': ep({
        method: 'GET',
        path: '/v1/me',
        summary: 'Current user, role, permissions, entitlements, workspaces',
        owner: 'API-A',
        auth: 'tenant',
        response: MeSchema,
    }),
    'me.update': ep({
        method: 'PATCH',
        path: '/v1/me',
        summary: 'Update profile (name, avatar, timezone, theme)',
        owner: 'API-A',
        auth: 'tenant',
        body: ProfileUpdateSchema,
        response: MeSchema,
    }),
    'me.changePassword': ep({
        method: 'POST',
        path: '/v1/me/password',
        summary: 'Change password (requires current)',
        owner: 'API-A',
        auth: 'tenant',
        body: ChangePasswordSchema,
        response: OkSchema,
    }),
    'me.changeEmail': ep({
        method: 'POST',
        path: '/v1/me/email',
        summary: 'Start email change (verify new address)',
        owner: 'API-A',
        auth: 'tenant',
        body: ChangeEmailSchema,
        response: OkSchema,
    }),
    'me.cancelEmailChange': ep({
        method: 'DELETE',
        path: '/v1/me/email',
        summary: 'Cancel pending email change',
        owner: 'API-A',
        auth: 'tenant',
        response: OkSchema,
    }),
    'me.sessions': ep({
        method: 'GET',
        path: '/v1/me/sessions',
        summary: 'Active sessions (devices)',
        owner: 'API-A',
        auth: 'tenant',
        response: list(SessionInfoSchema),
    }),
    'me.revokeSession': ep({
        method: 'DELETE',
        path: '/v1/me/sessions/:id',
        summary: 'Sign out one device',
        owner: 'API-A',
        auth: 'tenant',
        params: P.id,
        response: OkSchema,
    }),
    'me.revokeOtherSessions': ep({
        method: 'POST',
        path: '/v1/me/sessions/revoke-others',
        summary: 'Sign out all other devices',
        owner: 'API-A',
        auth: 'tenant',
        response: CountResult,
    }),
    'me.delete': ep({
        method: 'POST',
        path: '/v1/me/delete',
        summary: 'Schedule account deletion (30-day grace). 409 sole_owner',
        owner: 'API-A',
        auth: 'tenant',
        body: DeleteAccountSchema,
        response: DeletionScheduledSchema,
    }),
    'me.cancelDelete': ep({
        method: 'POST',
        path: '/v1/me/delete/cancel',
        summary: 'Cancel scheduled account deletion',
        owner: 'API-A',
        auth: 'tenant',
        response: OkSchema,
    }),
    'members.list': ep({
        method: 'GET',
        path: '/v1/members',
        summary: 'Members (search/filter)',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['user.view'],
        query: PageQuerySchema.merge(MemberListQuery),
        response: pageOf(MemberSchema),
        paginated: true,
    }),
    'members.get': ep({
        method: 'GET',
        path: '/v1/members/:id',
        summary: 'One member',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['user.view'],
        params: P.id,
        response: MemberSchema,
    }),
    'members.update': ep({
        method: 'PATCH',
        path: '/v1/members/:id',
        summary: 'Change role / workspaces',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['user.role.change'],
        params: P.id,
        body: MemberUpdateSchema,
        response: MemberSchema,
    }),
    'members.deactivate': ep({
        method: 'POST',
        path: '/v1/members/:id/deactivate',
        summary: 'Block login, free seat, optional reassign',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['user.deactivate'],
        params: P.id,
        body: MemberRemoveSchema,
        response: MemberSchema,
    }),
    'members.reactivate': ep({
        method: 'POST',
        path: '/v1/members/:id/reactivate',
        summary: 'Re-enable a deactivated member',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['user.deactivate'],
        entitlement: 'max_users',
        params: P.id,
        response: MemberSchema,
    }),
    'members.remove': ep({
        method: 'POST',
        path: '/v1/members/:id/remove',
        summary: 'Remove (anonymised after 30 days), reassign open missions',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['user.remove'],
        params: P.id,
        body: MemberRemoveSchema,
        response: OkSchema,
    }),
    'members.transferOwnership': ep({
        method: 'POST',
        path: '/v1/tenant/transfer-ownership',
        summary: 'Transfer Owner role',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['tenant.ownership.transfer'],
        body: TransferOwnershipSchema,
        response: OkSchema,
    }),
    'invitations.list': ep({
        method: 'GET',
        path: '/v1/invitations',
        summary: 'Pending/expired invitations',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['user.invite'],
        query: PageQuerySchema,
        response: pageOf(InvitationSchema),
        paginated: true,
    }),
    'invitations.create': ep({
        method: 'POST',
        path: '/v1/invitations',
        summary: 'Invite teammates (counts on accept)',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['user.invite'],
        entitlement: 'max_users',
        body: InviteCreateSchema,
        response: InviteCreateResultSchema,
    }),
    'invitations.resend': ep({
        method: 'POST',
        path: '/v1/invitations/:id/resend',
        summary: 'Resend + extend 7 days',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['user.invite'],
        params: P.id,
        response: InvitationSchema,
    }),
    'invitations.revoke': ep({
        method: 'DELETE',
        path: '/v1/invitations/:id',
        summary: 'Revoke invitation',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['user.invite'],
        params: P.id,
        response: OkSchema,
    }),
    'roles.matrix': ep({
        method: 'GET',
        path: '/v1/roles',
        summary: 'Roles + permission matrix',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['user.view'],
        response: PermissionMatrixSchema,
    }),
    'roles.create': ep({
        method: 'POST',
        path: '/v1/roles',
        summary: 'Create/duplicate a custom role',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['role.manage'],
        entitlement: 'custom_roles',
        body: RoleCreateSchema,
        response: RoleSchema,
    }),
    'roles.update': ep({
        method: 'PATCH',
        path: '/v1/roles/:id',
        summary: 'Rename / change permissions (custom roles)',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['role.manage'],
        entitlement: 'custom_roles',
        params: P.id,
        body: RoleUpdateSchema,
        response: RoleSchema,
    }),
    'roles.delete': ep({
        method: 'DELETE',
        path: '/v1/roles/:id',
        summary: 'Delete custom role (members move to reassignToRoleId)',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['role.manage'],
        params: P.id,
        query: z.object({ reassignToRoleId: Id }),
        response: OkSchema,
    }),
    'audit.list': ep({
        method: 'GET',
        path: '/v1/audit',
        summary: 'Company audit log',
        owner: 'API-A',
        auth: 'tenant',
        permission: ['tenant.audit.view'],
        query: AuditQuery.omit({ tenantId: true }),
        response: pageOf(AuditEntrySchema),
        paginated: true,
    }),
    /* ===================== Mission HQ auth & team (API-A) ===================== */
    'hq.auth.login': ep({
        method: 'POST',
        path: '/v1/hq/auth/login',
        summary: 'Platform sign-in step 1 (password)',
        owner: 'API-A',
        auth: 'public',
        body: PlatformLoginSchema,
        response: PlatformLoginResponseSchema,
    }),
    'hq.auth.totp': ep({
        method: 'POST',
        path: '/v1/hq/auth/totp',
        summary: 'Platform sign-in step 2 (TOTP)',
        owner: 'API-A',
        auth: 'public',
        body: PlatformTotpVerifySchema,
        response: PlatformSessionSchema,
    }),
    'hq.auth.refresh': ep({
        method: 'POST',
        path: '/v1/hq/auth/refresh',
        summary: 'Rotate platform refresh cookie',
        owner: 'API-A',
        auth: 'public',
        response: RefreshResponseSchema,
    }),
    'hq.auth.logout': ep({
        method: 'POST',
        path: '/v1/hq/auth/logout',
        summary: 'Platform sign-out',
        owner: 'API-A',
        auth: 'platform',
        response: OkSchema,
    }),
    'hq.auth.setupPreview': ep({
        method: 'GET',
        path: '/v1/hq/auth/setup/:token',
        summary: 'One-time setup link state',
        owner: 'API-A',
        auth: 'public',
        params: P.token,
        response: PlatformSetupPreviewSchema,
    }),
    'hq.auth.setup': ep({
        method: 'POST',
        path: '/v1/hq/auth/setup',
        summary: 'Set password from setup link → session (TOTP enrol next)',
        owner: 'API-A',
        auth: 'public',
        body: PlatformSetupSchema,
        response: PlatformSessionSchema,
    }),
    'hq.me': ep({
        method: 'GET',
        path: '/v1/hq/me',
        summary: 'Current platform user',
        owner: 'API-A',
        auth: 'platform',
        response: PlatformMeSchema,
    }),
    'hq.totp.enrol': ep({
        method: 'POST',
        path: '/v1/hq/me/totp/enrol',
        summary: 'Start TOTP enrolment (secret + QR)',
        owner: 'API-A',
        auth: 'platform',
        response: TotpEnrolSchema,
    }),
    'hq.totp.confirm': ep({
        method: 'POST',
        path: '/v1/hq/me/totp/confirm',
        summary: 'Confirm TOTP code, enable 2FA',
        owner: 'API-A',
        auth: 'platform',
        body: TotpConfirmSchema,
        response: OkSchema,
    }),
    'hq.team.list': ep({
        method: 'GET',
        path: '/v1/hq/team',
        summary: 'Platform team',
        owner: 'API-A',
        auth: 'platform',
        permission: ['hq.overview.view'],
        response: list(PlatformUserSchema),
    }),
    'hq.team.invite': ep({
        method: 'POST',
        path: '/v1/hq/team',
        summary: 'Invite platform user → setup link',
        owner: 'API-A',
        auth: 'platform',
        permission: ['hq.team.manage'],
        body: PlatformUserInviteSchema,
        response: PlatformUserInviteResultSchema,
    }),
    'hq.team.update': ep({
        method: 'PATCH',
        path: '/v1/hq/team/:id',
        summary: 'Change role / disable',
        owner: 'API-A',
        auth: 'platform',
        permission: ['hq.team.manage'],
        params: P.id,
        body: PlatformUserUpdateSchema,
        response: PlatformUserSchema,
    }),
    'hq.team.remove': ep({
        method: 'DELETE',
        path: '/v1/hq/team/:id',
        summary: 'Remove platform user',
        owner: 'API-A',
        auth: 'platform',
        permission: ['hq.team.manage'],
        params: P.id,
        response: OkSchema,
    }),
    'hq.audit.list': ep({
        method: 'GET',
        path: '/v1/hq/audit',
        summary: 'Platform + tenant audit log',
        owner: 'API-A',
        auth: 'platform',
        permission: ['hq.audit.view'],
        query: AuditQuery,
        response: pageOf(AuditEntrySchema),
        paginated: true,
    }),
    /* ===================== Public: tenant config, offers, claim (API-B) ===================== */
    'public.tenantConfig': ep({
        method: 'GET',
        path: '/v1/public/tenant-config',
        summary: 'Branding by Host (or ?slug= on the temp link)',
        owner: 'API-B',
        auth: 'public',
        query: z.object({ slug: z.string().optional() }),
        response: TenantPublicConfigSchema,
    }),
    'public.offer': ep({
        method: 'GET',
        path: '/v1/public/offers/:slug',
        summary: 'Offer landing page data',
        owner: 'API-B',
        auth: 'public',
        params: P.slug,
        response: PublicOfferSchema,
    }),
    'public.offerOpened': ep({
        method: 'POST',
        path: '/v1/public/offers/:slug/opened',
        summary: 'Track open (funnel)',
        owner: 'API-B',
        auth: 'public',
        params: P.slug,
        response: OkSchema,
    }),
    'public.slugAvailability': ep({
        method: 'GET',
        path: '/v1/public/slug-availability',
        summary: 'Temp-link slug check (debounced)',
        owner: 'API-B',
        auth: 'public',
        query: z.object({ slug: z.string() }),
        response: SlugAvailabilitySchema,
    }),
    'public.offerClaim': ep({
        method: 'POST',
        path: '/v1/public/offers/:slug/claim',
        summary: 'Claim: create tenant + owner + gift subscription, signs in',
        owner: 'API-B',
        auth: 'public',
        params: P.slug,
        body: OfferClaimSchema,
        response: AuthSessionSchema,
    }),
    /* ===================== Company settings (API-B) ===================== */
    'tenant.get': ep({
        method: 'GET',
        path: '/v1/tenant',
        summary: 'Company summary',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.view'],
        response: TenantSchema,
    }),
    'tenant.update': ep({
        method: 'PATCH',
        path: '/v1/tenant',
        summary: 'Company profile (onboarding step 2)',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.branding.edit'],
        body: TenantUpdateSchema,
        response: TenantSchema,
    }),
    'tenant.delete': ep({
        method: 'POST',
        path: '/v1/tenant/delete',
        summary: 'Schedule company deletion (30-day grace)',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.delete'],
        body: CompanyDeleteSchema,
        response: DeletionScheduledSchema,
    }),
    'tenant.cancelDelete': ep({
        method: 'POST',
        path: '/v1/tenant/delete/cancel',
        summary: 'Cancel company deletion',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.delete'],
        response: OkSchema,
    }),
    'branding.get': ep({
        method: 'GET',
        path: '/v1/tenant/branding',
        summary: 'Branding settings',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.view'],
        response: BrandingSchema,
    }),
    'branding.update': ep({
        method: 'PATCH',
        path: '/v1/tenant/branding',
        summary: 'Update branding (color, font, logos, mission language…)',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.branding.edit'],
        body: BrandingUpdateSchema,
        response: BrandingSchema,
    }),
    'domains.list': ep({
        method: 'GET',
        path: '/v1/tenant/domains',
        summary: 'Custom domains + DNS records',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.domain.manage'],
        response: list(TenantDomainSchema),
    }),
    'domains.add': ep({
        method: 'POST',
        path: '/v1/tenant/domains',
        summary: 'Add own domain → DNS records',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.domain.manage'],
        body: DomainAddSchema,
        response: TenantDomainSchema,
    }),
    'domains.verify': ep({
        method: 'POST',
        path: '/v1/tenant/domains/:id/verify',
        summary: 'Re-check DNS now',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.domain.manage'],
        params: P.id,
        response: TenantDomainSchema,
    }),
    'domains.makePrimary': ep({
        method: 'POST',
        path: '/v1/tenant/domains/:id/primary',
        summary: 'Use as primary (temp link redirects)',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.domain.manage'],
        params: P.id,
        response: TenantDomainSchema,
    }),
    'domains.remove': ep({
        method: 'DELETE',
        path: '/v1/tenant/domains/:id',
        summary: 'Remove domain',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.domain.manage'],
        params: P.id,
        response: OkSchema,
    }),
    'domains.sendInstructions': ep({
        method: 'POST',
        path: '/v1/tenant/domains/:id/send-instructions',
        summary: 'Email DNS records to IT',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.domain.manage'],
        params: P.id,
        body: DomainInstructionsEmailSchema,
        response: OkSchema,
    }),
    'billing.get': ep({
        method: 'GET',
        path: '/v1/tenant/billing',
        summary: 'Plan, usage, entitlements, open inquiry',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.billing.view'],
        response: BillingSummarySchema,
    }),
    'billing.requestPlan': ep({
        method: 'POST',
        path: '/v1/tenant/billing/inquiries',
        summary: 'Request plan change → Mission HQ inbox',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.billing.request'],
        body: PlanInquiryCreateSchema,
        response: PlanInquirySchema,
    }),
    'billing.cancelInquiry': ep({
        method: 'DELETE',
        path: '/v1/tenant/billing/inquiries/:id',
        summary: 'Withdraw a plan request',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.billing.request'],
        params: P.id,
        response: OkSchema,
    }),
    'exports.list': ep({
        method: 'GET',
        path: '/v1/tenant/exports',
        summary: 'Data exports',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.export'],
        response: list(DataExportSchema),
    }),
    'exports.create': ep({
        method: 'POST',
        path: '/v1/tenant/exports',
        summary: 'Queue a full export (zip, link valid 24h)',
        owner: 'API-B',
        auth: 'tenant',
        permission: ['tenant.export'],
        entitlement: 'data_export',
        response: DataExportSchema,
    }),
    'uploads.presign': ep({
        method: 'POST',
        path: '/v1/uploads/presign',
        summary: 'Presigned R2 PUT (size/type checked vs plan)',
        owner: 'API-B',
        auth: 'tenant',
        body: PresignRequestSchema,
        response: PresignResponseSchema,
    }),
    'uploads.complete': ep({
        method: 'POST',
        path: '/v1/uploads/complete',
        summary: 'Mark upload finished (HEAD check)',
        owner: 'API-B',
        auth: 'tenant',
        body: UploadCompleteSchema,
        response: OkSchema,
    }),
    /* ===================== Mission HQ: tenants, plans, offers, ops (API-B) ===================== */
    'hq.overview': ep({
        method: 'GET',
        path: '/v1/hq/overview',
        summary: 'KPIs, expiring soon, recent audit',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.overview.view'],
        response: HqOverviewSchema,
    }),
    'hq.tenants.list': ep({
        method: 'GET',
        path: '/v1/hq/tenants',
        summary: 'Tenants table',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.view'],
        query: HqTenantListQuery,
        response: pageOf(HqTenantRowSchema),
        paginated: true,
    }),
    'hq.tenants.create': ep({
        method: 'POST',
        path: '/v1/hq/tenants',
        summary: 'Onboard tenant → copyable invite link',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.manage', 'hq.subscription.manage'],
        body: HqTenantCreateSchema,
        response: HqTenantCreateResultSchema,
    }),
    'hq.tenants.get': ep({
        method: 'GET',
        path: '/v1/hq/tenants/:id',
        summary: 'Tenant detail',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.view'],
        params: P.id,
        response: HqTenantDetailSchema,
    }),
    'hq.tenants.update': ep({
        method: 'PATCH',
        path: '/v1/hq/tenants/:id',
        summary: 'Rename / change slug',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.manage'],
        params: P.id,
        body: z.object({ name: z.string().min(2).max(80).optional(), slug: Slug.optional() }),
        response: HqTenantDetailSchema,
    }),
    'hq.tenants.branding': ep({
        method: 'PATCH',
        path: '/v1/hq/tenants/:id/branding',
        summary: 'Edit tenant branding',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.manage'],
        params: P.id,
        body: BrandingUpdateSchema,
        response: BrandingSchema,
    }),
    'hq.tenants.suspend': ep({
        method: 'POST',
        path: '/v1/hq/tenants/:id/suspend',
        summary: 'Suspend (reason required)',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.manage'],
        params: P.id,
        body: HqTenantActionSchema,
        response: HqTenantDetailSchema,
    }),
    'hq.tenants.restore': ep({
        method: 'POST',
        path: '/v1/hq/tenants/:id/restore',
        summary: 'Restore suspended / pending-deletion tenant',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.manage'],
        params: P.id,
        response: HqTenantDetailSchema,
    }),
    'hq.tenants.scheduleDeletion': ep({
        method: 'POST',
        path: '/v1/hq/tenants/:id/delete',
        summary: 'Schedule deletion (type name + reason)',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.delete'],
        params: P.id,
        body: HqTenantDeleteSchema,
        response: HqTenantDetailSchema,
    }),
    'hq.tenants.enter': ep({
        method: 'POST',
        path: '/v1/hq/tenants/:id/enter',
        summary: 'Enter as DevNinjaWeb staff (full access, audited)',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.enter'],
        params: P.id,
        body: HqTenantActionSchema,
        response: HqEnterTenantResultSchema,
    }),
    'hq.tenants.users': ep({
        method: 'GET',
        path: '/v1/hq/tenants/:id/users',
        summary: 'Tenant users',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.view'],
        params: P.id,
        response: HqTenantUsersSchema,
    }),
    'hq.tenants.resetOwner': ep({
        method: 'POST',
        path: '/v1/hq/tenants/:id/reset-owner',
        summary: 'New owner setup link',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.manage'],
        params: P.id,
        response: SetupUrlResult,
    }),
    'hq.tenants.recheckDomain': ep({
        method: 'POST',
        path: '/v1/hq/tenants/:id/domains/:domainId/recheck',
        summary: 'Re-check DNS/SSL',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.manage'],
        params: P.tenantDomain,
        response: TenantDomainSchema,
    }),
    'hq.tenants.export': ep({
        method: 'POST',
        path: '/v1/hq/tenants/:id/export',
        summary: 'Export tenant data',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.manage'],
        params: P.id,
        response: DataExportSchema,
    }),
    'hq.subscriptions.assign': ep({
        method: 'POST',
        path: '/v1/hq/tenants/:id/subscription',
        summary: 'Assign/change plan (gift/paid/trial)',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.subscription.manage'],
        params: P.id,
        body: SubscriptionAssignSchema,
        response: SubscriptionSchema,
    }),
    'hq.subscriptions.extend': ep({
        method: 'POST',
        path: '/v1/hq/tenants/:id/subscription/extend',
        summary: 'Extend +months or to a date',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.subscription.manage'],
        params: P.id,
        body: SubscriptionExtendSchema,
        response: SubscriptionSchema,
    }),
    'hq.entitlements.get': ep({
        method: 'GET',
        path: '/v1/hq/tenants/:id/entitlements',
        summary: 'Effective entitlements for company or a workspace',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.tenant.view'],
        params: P.id,
        query: EntitlementScopeQuery,
        response: list(EffectiveEntitlementSchema),
    }),
    'hq.entitlements.set': ep({
        method: 'PUT',
        path: '/v1/hq/tenants/:id/entitlements',
        summary: 'Set override (reason required, optional expiry)',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.entitlement.override'],
        params: P.id,
        body: EntitlementOverrideSetSchema,
        response: EffectiveEntitlementSchema,
    }),
    'hq.entitlements.reset': ep({
        method: 'DELETE',
        path: '/v1/hq/tenants/:id/entitlements/:overrideId',
        summary: 'Reset one override to inherited',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.entitlement.override'],
        params: P.tenantOverride,
        response: EffectiveEntitlementSchema,
    }),
    'hq.entitlements.resetAll': ep({
        method: 'POST',
        path: '/v1/hq/tenants/:id/entitlements/reset',
        summary: 'Reset all overrides in a scope',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.entitlement.override'],
        params: P.id,
        body: EntitlementScopeQuery,
        response: CountResult,
    }),
    'hq.plans.list': ep({
        method: 'GET',
        path: '/v1/hq/plans',
        summary: 'Plans catalog (incl. archived)',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.overview.view'],
        response: list(PlanSchema),
    }),
    'hq.plans.create': ep({
        method: 'POST',
        path: '/v1/hq/plans',
        summary: 'New plan (draft v1)',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.plan.manage'],
        body: PlanCreateSchema,
        response: PlanSchema,
    }),
    'hq.plans.get': ep({
        method: 'GET',
        path: '/v1/hq/plans/:id',
        summary: 'Plan with draft entitlements',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.overview.view'],
        params: P.id,
        response: PlanSchema,
    }),
    'hq.plans.update': ep({
        method: 'PATCH',
        path: '/v1/hq/plans/:id',
        summary: 'Rename / edit draft entitlements',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.plan.manage'],
        params: P.id,
        body: PlanUpdateSchema,
        response: PlanSchema,
    }),
    'hq.plans.publish': ep({
        method: 'POST',
        path: '/v1/hq/plans/:id/publish',
        summary: 'Publish draft as new version (keep or migrate tenants)',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.plan.manage'],
        params: P.id,
        body: PlanPublishSchema,
        response: PlanSchema,
    }),
    'hq.plans.versions': ep({
        method: 'GET',
        path: '/v1/hq/plans/:id/versions',
        summary: 'Version history',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.overview.view'],
        params: P.id,
        response: list(PlanVersionSchema),
    }),
    'hq.plans.duplicate': ep({
        method: 'POST',
        path: '/v1/hq/plans/:id/duplicate',
        summary: 'Duplicate plan',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.plan.manage'],
        params: P.id,
        response: PlanSchema,
    }),
    'hq.plans.archive': ep({
        method: 'POST',
        path: '/v1/hq/plans/:id/archive',
        summary: 'Archive (no new assignments)',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.plan.manage'],
        params: P.id,
        response: PlanSchema,
    }),
    'hq.plans.restore': ep({
        method: 'POST',
        path: '/v1/hq/plans/:id/restore',
        summary: 'Un-archive',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.plan.manage'],
        params: P.id,
        response: PlanSchema,
    }),
    'hq.plans.delete': ep({
        method: 'DELETE',
        path: '/v1/hq/plans/:id',
        summary: 'Delete a never-published draft',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.plan.manage'],
        params: P.id,
        response: OkSchema,
    }),
    'hq.plans.reorder': ep({
        method: 'POST',
        path: '/v1/hq/plans/reorder',
        summary: 'Reorder plans',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.plan.manage'],
        body: PlanReorderSchema,
        response: list(PlanSchema),
    }),
    'hq.inquiries.list': ep({
        method: 'GET',
        path: '/v1/hq/inquiries',
        summary: 'Plan inquiries inbox',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.inquiry.manage'],
        query: PageQuerySchema.extend({ status: z.enum(['new', 'contacted', 'approved', 'rejected']).optional() }),
        response: pageOf(PlanInquirySchema),
        paginated: true,
    }),
    'hq.inquiries.update': ep({
        method: 'PATCH',
        path: '/v1/hq/inquiries/:id',
        summary: 'Change status',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.inquiry.manage'],
        params: P.id,
        body: PlanInquiryUpdateSchema,
        response: PlanInquirySchema,
    }),
    'hq.inquiries.approve': ep({
        method: 'POST',
        path: '/v1/hq/inquiries/:id/approve',
        summary: 'Approve & assign subscription',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.inquiry.manage', 'hq.subscription.manage'],
        params: P.id,
        body: SubscriptionAssignSchema,
        response: SubscriptionSchema,
    }),
    'hq.offers.list': ep({
        method: 'GET',
        path: '/v1/hq/offers',
        summary: 'Offer links',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.offer.manage'],
        query: PageQuerySchema.merge(SearchQuery).extend({
            status: z.enum(['active', 'claimed', 'expired', 'revoked']).optional(),
        }),
        response: pageOf(OfferLinkSchema),
        paginated: true,
    }),
    'hq.offers.create': ep({
        method: 'POST',
        path: '/v1/hq/offers',
        summary: 'Create offer link',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.offer.manage'],
        body: OfferLinkCreateSchema,
        response: OfferLinkSchema,
    }),
    'hq.offers.get': ep({
        method: 'GET',
        path: '/v1/hq/offers/:id',
        summary: 'Offer link detail',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.offer.manage'],
        params: P.id,
        response: OfferLinkSchema,
    }),
    'hq.offers.update': ep({
        method: 'PATCH',
        path: '/v1/hq/offers/:id',
        summary: 'Rename / note / claim-by',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.offer.manage'],
        params: P.id,
        body: OfferLinkUpdateSchema,
        response: OfferLinkSchema,
    }),
    'hq.offers.share': ep({
        method: 'GET',
        path: '/v1/hq/offers/:id/share',
        summary: 'Share text, WhatsApp URL, QR matrix',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.offer.manage'],
        params: P.id,
        response: OfferShareSchema,
    }),
    'hq.offers.duplicate': ep({
        method: 'POST',
        path: '/v1/hq/offers/:id/duplicate',
        summary: 'Duplicate link',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.offer.manage'],
        params: P.id,
        response: OfferLinkSchema,
    }),
    'hq.offers.revoke': ep({
        method: 'POST',
        path: '/v1/hq/offers/:id/revoke',
        summary: 'Revoke (Undo via restore)',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.offer.manage'],
        params: P.id,
        response: OfferLinkSchema,
    }),
    'hq.offers.restore': ep({
        method: 'POST',
        path: '/v1/hq/offers/:id/restore',
        summary: 'Undo revoke',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.offer.manage'],
        params: P.id,
        response: OfferLinkSchema,
    }),
    'hq.jobs.list': ep({
        method: 'GET',
        path: '/v1/hq/jobs',
        summary: 'Jobs with last run + next run',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.overview.view'],
        response: list(JobSchema),
    }),
    'hq.jobs.runs': ep({
        method: 'GET',
        path: '/v1/hq/jobs/:name/runs',
        summary: 'Run history',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.overview.view'],
        params: P.jobName,
        query: PageQuerySchema,
        response: pageOf(JobRunSchema),
        paginated: true,
    }),
    'hq.jobs.run': ep({
        method: 'POST',
        path: '/v1/hq/jobs/:name/run',
        summary: 'Run now',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.jobs.run'],
        params: P.jobName,
        response: JobRunSchema,
    }),
    'hq.releases.list': ep({
        method: 'GET',
        path: '/v1/hq/releases',
        summary: 'Releases',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.overview.view'],
        response: list(ReleaseSchema),
    }),
    'hq.releases.create': ep({
        method: 'POST',
        path: '/v1/hq/releases',
        summary: 'Draft release notes',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.release.publish'],
        body: ReleaseCreateSchema,
        response: ReleaseSchema,
    }),
    'hq.releases.publish': ep({
        method: 'POST',
        path: '/v1/hq/releases/:id/publish',
        summary: 'Publish → SSE release.published (blocking refresh)',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.release.publish'],
        params: P.id,
        response: ReleaseSchema,
    }),
    'hq.broadcasts.list': ep({
        method: 'GET',
        path: '/v1/hq/broadcasts',
        summary: 'Broadcast announcements',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.overview.view'],
        query: PageQuerySchema,
        response: pageOf(BroadcastSchema),
        paginated: true,
    }),
    'hq.broadcasts.create': ep({
        method: 'POST',
        path: '/v1/hq/broadcasts',
        summary: 'Send/schedule announcement',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.broadcast.send'],
        body: BroadcastCreateSchema,
        response: BroadcastSchema,
    }),
    'hq.broadcasts.cancel': ep({
        method: 'POST',
        path: '/v1/hq/broadcasts/:id/cancel',
        summary: 'Cancel scheduled broadcast',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.broadcast.send'],
        params: P.id,
        response: BroadcastSchema,
    }),
    'hq.freeTier': ep({
        method: 'GET',
        path: '/v1/hq/system/free-tier',
        summary: 'Keep-warm, DB expiry countdown, last backup',
        owner: 'API-B',
        auth: 'platform',
        permission: ['hq.overview.view'],
        response: FreeTierStatusSchema,
    }),
    /* ===================== Work structure (API-C) ===================== */
    'workspaces.list': ep({
        method: 'GET',
        path: '/v1/workspaces',
        summary: 'Workspaces I can see',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['workspace.view'],
        response: list(WorkspaceSchema),
    }),
    'workspaces.create': ep({
        method: 'POST',
        path: '/v1/workspaces',
        summary: 'Create workspace',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['workspace.create'],
        entitlement: 'max_workspaces',
        body: WorkspaceCreateSchema,
        response: WorkspaceSchema,
    }),
    'workspaces.update': ep({
        method: 'PATCH',
        path: '/v1/workspaces/:id',
        summary: 'Rename / reorder',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['workspace.edit'],
        params: P.id,
        body: WorkspaceUpdateSchema,
        response: WorkspaceSchema,
    }),
    'workspaces.delete': ep({
        method: 'POST',
        path: '/v1/workspaces/:id/delete',
        summary: 'Delete (type name)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['workspace.delete'],
        params: P.id,
        body: WorkspaceDeleteSchema,
        response: OkSchema,
    }),
    'workspaces.members': ep({
        method: 'GET',
        path: '/v1/workspaces/:id/members',
        summary: 'Workspace members',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['workspace.view'],
        params: P.id,
        response: list(UserRefSchema),
    }),
    'workspaces.setMembers': ep({
        method: 'PUT',
        path: '/v1/workspaces/:id/members',
        summary: 'Replace workspace members',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['workspace.member.manage'],
        params: P.id,
        body: ProjectMembersSchema,
        response: list(UserRefSchema),
    }),
    'folders.list': ep({
        method: 'GET',
        path: '/v1/folders',
        summary: 'Folders in a workspace',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.view'],
        query: z.object({ workspaceId: Id }),
        response: list(FolderSchema),
    }),
    'folders.create': ep({
        method: 'POST',
        path: '/v1/folders',
        summary: 'Create folder',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.create'],
        body: FolderCreateSchema,
        response: FolderSchema,
    }),
    'folders.update': ep({
        method: 'PATCH',
        path: '/v1/folders/:id',
        summary: 'Rename / reorder folder',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.edit'],
        params: P.id,
        body: FolderUpdateSchema,
        response: FolderSchema,
    }),
    'folders.delete': ep({
        method: 'DELETE',
        path: '/v1/folders/:id',
        summary: 'Delete folder (projects move to root)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.edit'],
        params: P.id,
        response: OkSchema,
    }),
    'clients.list': ep({
        method: 'GET',
        path: '/v1/clients',
        summary: 'Clients',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.view'],
        response: list(ClientSchema),
    }),
    'clients.create': ep({
        method: 'POST',
        path: '/v1/clients',
        summary: 'Create client',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['client.manage'],
        body: ClientUpsertSchema,
        response: ClientSchema,
    }),
    'clients.update': ep({
        method: 'PATCH',
        path: '/v1/clients/:id',
        summary: 'Rename client',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['client.manage'],
        params: P.id,
        body: ClientUpsertSchema,
        response: ClientSchema,
    }),
    'clients.delete': ep({
        method: 'DELETE',
        path: '/v1/clients/:id',
        summary: 'Delete client (projects keep running)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['client.manage'],
        params: P.id,
        response: OkSchema,
    }),
    'projects.list': ep({
        method: 'GET',
        path: '/v1/projects',
        summary: 'Projects',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.view'],
        query: PageQuerySchema.merge(ProjectListQuery),
        response: pageOf(ProjectSchema),
        paginated: true,
    }),
    'projects.tree': ep({
        method: 'GET',
        path: '/v1/projects/tree',
        summary: 'Sidebar tree: folders → projects',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.view'],
        query: z.object({ workspaceId: Id }),
        response: z.object({
            folders: z.array(FolderSchema.extend({
                projects: z.array(ProjectSchema.pick({ id: true, name: true, color: true, code: true, counts: true })),
            })),
            rootProjects: z.array(ProjectSchema.pick({ id: true, name: true, color: true, code: true, counts: true })),
        }),
    }),
    'projects.get': ep({
        method: 'GET',
        path: '/v1/projects/:id',
        summary: 'Project with statuses + members',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.view'],
        params: P.id,
        response: ProjectSchema,
    }),
    'projects.create': ep({
        method: 'POST',
        path: '/v1/projects',
        summary: 'Create project (default statuses)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.create'],
        entitlement: 'max_projects',
        body: ProjectCreateSchema,
        response: ProjectSchema,
    }),
    'projects.update': ep({
        method: 'PATCH',
        path: '/v1/projects/:id',
        summary: 'Edit / archive project',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.edit'],
        params: P.id,
        body: ProjectUpdateSchema,
        response: ProjectSchema,
    }),
    'projects.delete': ep({
        method: 'POST',
        path: '/v1/projects/:id/delete',
        summary: 'Delete permanently (type name)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.delete'],
        params: P.id,
        body: ProjectDeleteSchema,
        response: OkSchema,
    }),
    'projects.setMembers': ep({
        method: 'PUT',
        path: '/v1/projects/:id/members',
        summary: 'Replace project members (crew)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.member.manage'],
        params: P.id,
        body: ProjectMembersSchema,
        response: list(UserRefSchema),
    }),
    'statuses.list': ep({
        method: 'GET',
        path: '/v1/projects/:projectId/statuses',
        summary: 'Project statuses',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.view'],
        params: P.projectId,
        response: list(StatusSchema),
    }),
    'statuses.create': ep({
        method: 'POST',
        path: '/v1/projects/:projectId/statuses',
        summary: 'New status',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['status.manage'],
        params: P.projectId,
        body: StatusCreateSchema,
        response: StatusSchema,
    }),
    'statuses.update': ep({
        method: 'PATCH',
        path: '/v1/statuses/:id',
        summary: 'Rename / recolor / closed flag',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['status.manage'],
        params: P.id,
        body: StatusUpdateSchema,
        response: StatusSchema,
    }),
    'statuses.delete': ep({
        method: 'POST',
        path: '/v1/statuses/:id/delete',
        summary: 'Delete status (move missions)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['status.manage'],
        params: P.id,
        body: StatusDeleteSchema,
        response: OkSchema,
    }),
    'statuses.reorder': ep({
        method: 'POST',
        path: '/v1/projects/:projectId/statuses/reorder',
        summary: 'Reorder statuses',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['status.manage'],
        params: P.projectId,
        body: z.object({ statusIds: z.array(Id).min(1) }),
        response: list(StatusSchema),
    }),
    'customFields.list': ep({
        method: 'GET',
        path: '/v1/custom-fields',
        summary: 'Custom fields (workspace + project)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['project.view'],
        query: z.object({ workspaceId: Id, projectId: Id.optional() }),
        response: list(CustomFieldSchema),
    }),
    'customFields.create': ep({
        method: 'POST',
        path: '/v1/custom-fields',
        summary: 'Add custom field (column)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['field.manage'],
        entitlement: 'custom_fields',
        body: CustomFieldCreateSchema,
        response: CustomFieldSchema,
    }),
    'customFields.update': ep({
        method: 'PATCH',
        path: '/v1/custom-fields/:id',
        summary: 'Edit field / options',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['field.manage'],
        params: P.id,
        body: CustomFieldUpdateSchema,
        response: CustomFieldSchema,
    }),
    'customFields.delete': ep({
        method: 'POST',
        path: '/v1/custom-fields/:id/delete',
        summary: 'Delete field + values (type name)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['field.manage'],
        params: P.id,
        body: CustomFieldDeleteSchema,
        response: OkSchema,
    }),
    'tags.list': ep({
        method: 'GET',
        path: '/v1/tags',
        summary: 'Tags in workspace',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        query: z.object({ workspaceId: Id }),
        response: list(TagSchema),
    }),
    'tags.create': ep({
        method: 'POST',
        path: '/v1/tags',
        summary: 'Create tag',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.edit'],
        body: TagCreateSchema,
        response: TagSchema,
    }),
    'tags.delete': ep({
        method: 'DELETE',
        path: '/v1/tags/:id',
        summary: 'Delete tag',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['field.manage'],
        params: P.id,
        response: OkSchema,
    }),
    /* ===================== Missions (API-C) ===================== */
    'missions.groups': ep({
        method: 'GET',
        path: '/v1/missions/groups',
        summary: 'Grouped list first screen (groups + first page each)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        query: MissionListQuery.omit({ cursor: true, groupValue: true }),
        response: MissionGroupsSchema,
    }),
    'missions.list': ep({
        method: 'GET',
        path: '/v1/missions',
        summary: 'Flat or per-group page (cursor)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        query: MissionListQuery,
        response: pageOf(MissionSummarySchema),
        paginated: true,
    }),
    'missions.get': ep({
        method: 'GET',
        path: '/v1/missions/:id',
        summary: 'Mission detail',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        params: P.id,
        response: MissionDetailSchema,
    }),
    'missions.byKey': ep({
        method: 'GET',
        path: '/v1/missions/key/:key',
        summary: 'Resolve ACM-142 → mission',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        params: z.object({ key: z.string().regex(/^[A-Z]{2,5}-\d+$/) }),
        response: MissionDetailSchema,
    }),
    'missions.create': ep({
        method: 'POST',
        path: '/v1/missions',
        summary: 'Create mission / objective',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.create'],
        entitlement: 'max_missions_per_project',
        body: MissionCreateSchema,
        response: MissionDetailSchema,
    }),
    'missions.update': ep({
        method: 'PATCH',
        path: '/v1/missions/:id',
        summary: 'Update fields (optimistic)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.edit'],
        params: P.id,
        body: MissionUpdateSchema,
        response: MissionDetailSchema,
    }),
    'missions.move': ep({
        method: 'POST',
        path: '/v1/missions/:id/move',
        summary: 'Drag & drop: status/position/project',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.edit'],
        params: P.id,
        body: MissionMoveSchema,
        response: MissionSummarySchema,
    }),
    'missions.delete': ep({
        method: 'DELETE',
        path: '/v1/missions/:id',
        summary: 'Move to trash (30 days) + undo token',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.delete'],
        params: P.id,
        response: UndoResult,
    }),
    'missions.restore': ep({
        method: 'POST',
        path: '/v1/missions/:id/restore',
        summary: 'Restore from trash',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.delete'],
        params: P.id,
        response: MissionDetailSchema,
    }),
    'missions.duplicate': ep({
        method: 'POST',
        path: '/v1/missions/:id/duplicate',
        summary: 'Duplicate mission',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.create'],
        params: P.id,
        response: MissionDetailSchema,
    }),
    'missions.bulk': ep({
        method: 'POST',
        path: '/v1/missions/bulk',
        summary: 'Bulk status/assign/due/priority/move/delete',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.bulk'],
        body: MissionBulkSchema,
        response: MissionBulkResultSchema,
    }),
    'missions.undo': ep({
        method: 'POST',
        path: '/v1/undo',
        summary: 'Undo the last delete / bulk op (30s token)',
        owner: 'API-C',
        auth: 'tenant',
        body: UndoSchema,
        response: OkSchema,
    }),
    'missions.watch': ep({
        method: 'PUT',
        path: '/v1/missions/:id/watch',
        summary: 'Watch / unwatch',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        params: P.id,
        body: z.object({ watching: z.boolean() }),
        response: z.object({ watching: z.boolean() }),
    }),
    'missions.setCustomValue': ep({
        method: 'PUT',
        path: '/v1/missions/:id/custom-values/:fieldId',
        summary: 'Set a custom field value',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.edit'],
        params: P.missionField,
        body: z.object({ value: CustomValue }),
        response: MissionSummarySchema,
    }),
    'checklist.create': ep({
        method: 'POST',
        path: '/v1/missions/:id/checklist',
        summary: 'Add checklist item',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.edit'],
        params: P.id,
        body: ChecklistCreateSchema,
        response: ChecklistItemSchema,
    }),
    'checklist.update': ep({
        method: 'PATCH',
        path: '/v1/checklist/:id',
        summary: 'Edit / tick checklist item',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.edit'],
        params: P.id,
        body: ChecklistUpdateSchema,
        response: ChecklistItemSchema,
    }),
    'checklist.delete': ep({
        method: 'DELETE',
        path: '/v1/checklist/:id',
        summary: 'Delete checklist item',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.edit'],
        params: P.id,
        response: OkSchema,
    }),
    'attachments.add': ep({
        method: 'POST',
        path: '/v1/missions/:id/attachments',
        summary: 'Attach an uploaded file',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['attachment.upload'],
        params: P.id,
        body: AttachmentCreateSchema,
        response: AttachmentSchema,
    }),
    'attachments.delete': ep({
        method: 'DELETE',
        path: '/v1/attachments/:id',
        summary: 'Remove attachment',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.edit'],
        params: P.id,
        response: OkSchema,
    }),
    'timeline.list': ep({
        method: 'GET',
        path: '/v1/missions/:id/timeline',
        summary: 'Activity + comments (newest first; UI shows oldest→newest)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        params: P.id,
        query: TimelineQuery,
        response: pageOf(TimelineItemSchema),
        paginated: true,
    }),
    'comments.create': ep({
        method: 'POST',
        path: '/v1/missions/:id/comments',
        summary: 'Comment (+ @mentions → notifications)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['comment.create'],
        params: P.id,
        body: CommentCreateSchema,
        response: CommentSchema,
    }),
    'comments.update': ep({
        method: 'PATCH',
        path: '/v1/comments/:id',
        summary: 'Edit own comment',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['comment.create'],
        params: P.id,
        body: CommentUpdateSchema,
        response: CommentSchema,
    }),
    'comments.delete': ep({
        method: 'DELETE',
        path: '/v1/comments/:id',
        summary: 'Delete comment',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['comment.create'],
        params: P.id,
        response: OkSchema,
    }),
    'comments.react': ep({
        method: 'POST',
        path: '/v1/comments/:id/reactions',
        summary: 'Add reaction',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['comment.create'],
        params: P.id,
        body: ReactionSchema,
        response: CommentSchema,
    }),
    'comments.unreact': ep({
        method: 'DELETE',
        path: '/v1/comments/:id/reactions/:emoji',
        summary: 'Remove reaction',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['comment.create'],
        params: P.commentEmoji,
        response: CommentSchema,
    }),
    'views.list': ep({
        method: 'GET',
        path: '/v1/views',
        summary: 'Saved views',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        query: z.object({ workspaceId: Id, projectId: Id.optional() }),
        response: list(ViewSchema),
    }),
    'views.create': ep({
        method: 'POST',
        path: '/v1/views',
        summary: 'Save view as…',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        body: ViewCreateSchema,
        response: ViewSchema,
    }),
    'views.update': ep({
        method: 'PATCH',
        path: '/v1/views/:id',
        summary: 'Update view',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        params: P.id,
        body: ViewUpdateSchema,
        response: ViewSchema,
    }),
    'views.delete': ep({
        method: 'DELETE',
        path: '/v1/views/:id',
        summary: 'Delete view',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        params: P.id,
        response: OkSchema,
    }),
    'search.global': ep({
        method: 'GET',
        path: '/v1/search',
        summary: 'Global search (missions, projects, people)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        query: z.object({ q: z.string().trim().min(1).max(200), limit: z.coerce.number().int().min(1).max(20).default(8) }),
        response: SearchResultSchema,
    }),
    'myMissions.list': ep({
        method: 'GET',
        path: '/v1/my-missions',
        summary: 'My Missions by bucket',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        query: MyMissionsQuery,
        response: pageOf(MissionSummarySchema),
        paginated: true,
    }),
    'myMissions.counts': ep({
        method: 'GET',
        path: '/v1/my-missions/counts',
        summary: 'Bucket counts',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        response: MyMissionsCountsSchema,
    }),
    'missionControl.get': ep({
        method: 'GET',
        path: '/v1/mission-control',
        summary: "Home dashboard (Today's briefing, projects, workload)",
        owner: 'API-C',
        auth: 'tenant',
        permission: ['mission.view'],
        query: z.object({ workspaceId: Id.optional() }),
        response: MissionControlSchema,
    }),
    /* ===================== Sticky boards (API-C) ===================== */
    'boards.list': ep({
        method: 'GET',
        path: '/v1/boards',
        summary: 'Boards by workspace/project',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.view'],
        entitlement: 'sticky_boards',
        query: z.object({ workspaceId: Id, projectId: Id.optional() }),
        response: list(StickyBoardSchema),
    }),
    'boards.create': ep({
        method: 'POST',
        path: '/v1/boards',
        summary: 'Create board',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.manage'],
        entitlement: 'max_sticky_boards',
        body: BoardCreateSchema,
        response: StickyBoardSchema,
    }),
    'boards.get': ep({
        method: 'GET',
        path: '/v1/boards/:id',
        summary: 'Board with frames + stickies',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.view'],
        entitlement: 'sticky_boards',
        params: P.id,
        response: BoardDetailSchema,
    }),
    'boards.update': ep({
        method: 'PATCH',
        path: '/v1/boards/:id',
        summary: 'Rename board',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.manage'],
        params: P.id,
        body: BoardUpdateSchema,
        response: StickyBoardSchema,
    }),
    'boards.delete': ep({
        method: 'POST',
        path: '/v1/boards/:id/delete',
        summary: 'Delete board (type name)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.manage'],
        params: P.id,
        body: BoardDeleteSchema,
        response: OkSchema,
    }),
    'boards.clear': ep({
        method: 'POST',
        path: '/v1/boards/:id/clear',
        summary: 'Clear all stickies (type "clear")',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.manage'],
        params: P.id,
        body: BoardClearSchema,
        response: UndoResult,
    }),
    'frames.create': ep({
        method: 'POST',
        path: '/v1/boards/:id/frames',
        summary: 'Add frame',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.edit'],
        params: P.id,
        body: FrameUpsertSchema,
        response: BoardFrameSchema,
    }),
    'frames.update': ep({
        method: 'PATCH',
        path: '/v1/frames/:id',
        summary: 'Rename / move / resize frame',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.edit'],
        params: P.id,
        body: FramePatchSchema,
        response: BoardFrameSchema,
    }),
    'frames.delete': ep({
        method: 'DELETE',
        path: '/v1/frames/:id',
        summary: 'Delete frame (stickies stay)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.edit'],
        params: P.id,
        response: OkSchema,
    }),
    'stickies.create': ep({
        method: 'POST',
        path: '/v1/boards/:id/stickies',
        summary: 'Add sticky',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.edit'],
        params: P.id,
        body: StickyCreateSchema,
        response: StickySchema,
    }),
    'stickies.update': ep({
        method: 'PATCH',
        path: '/v1/stickies/:id',
        summary: 'Edit text/color/position',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.edit'],
        params: P.id,
        body: StickyPatchSchema,
        response: StickySchema,
    }),
    'stickies.moveMany': ep({
        method: 'POST',
        path: '/v1/boards/:id/stickies/move',
        summary: 'Batch positions while dragging',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.edit'],
        params: P.id,
        body: StickyBatchMoveSchema,
        response: CountResult,
    }),
    'stickies.delete': ep({
        method: 'DELETE',
        path: '/v1/stickies/:id',
        summary: 'Delete sticky (Undo via restore)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.edit'],
        params: P.id,
        response: OkSchema,
    }),
    'stickies.restore': ep({
        method: 'POST',
        path: '/v1/stickies/:id/restore',
        summary: 'Undo sticky delete',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.edit'],
        params: P.id,
        response: StickySchema,
    }),
    'stickies.convert': ep({
        method: 'POST',
        path: '/v1/stickies/:id/convert',
        summary: 'Convert sticky → mission (links them)',
        owner: 'API-C',
        auth: 'tenant',
        permission: ['board.edit', 'mission.create'],
        params: P.id,
        body: StickyConvertSchema,
        response: z.object({ sticky: StickySchema, mission: MissionDetailSchema }),
    }),
    /* ===================== Notifications, push, stream (API-C) ===================== */
    'notifications.list': ep({
        method: 'GET',
        path: '/v1/notifications',
        summary: 'Inbox (All / Unread / Mentions / Assigned)',
        owner: 'API-C',
        auth: 'tenant',
        query: NotificationListQuery,
        response: pageOf(NotificationSchema),
        paginated: true,
    }),
    'notifications.counts': ep({
        method: 'GET',
        path: '/v1/notifications/counts',
        summary: 'Badge counts (unseen) + tab counts',
        owner: 'API-C',
        auth: 'tenant',
        response: NotificationCountsSchema,
    }),
    'notifications.seen': ep({
        method: 'POST',
        path: '/v1/notifications/seen',
        summary: 'Batch mark seen (visible ≥60% for 1s)',
        owner: 'API-C',
        auth: 'tenant',
        body: NotificationIdsSchema,
        response: NotificationCountsSchema,
    }),
    'notifications.read': ep({
        method: 'POST',
        path: '/v1/notifications/:id/read',
        summary: 'Mark read (clicked)',
        owner: 'API-C',
        auth: 'tenant',
        params: P.id,
        response: NotificationSchema,
    }),
    'notifications.unread': ep({
        method: 'POST',
        path: '/v1/notifications/:id/unread',
        summary: 'Mark unread',
        owner: 'API-C',
        auth: 'tenant',
        params: P.id,
        response: NotificationSchema,
    }),
    'notifications.readAll': ep({
        method: 'POST',
        path: '/v1/notifications/read-all',
        summary: 'Mark all as read (+ undo token)',
        owner: 'API-C',
        auth: 'tenant',
        response: ReadAllResultSchema,
    }),
    'notifications.undoReadAll': ep({
        method: 'POST',
        path: '/v1/notifications/read-all/undo',
        summary: 'Undo mark-all-read',
        owner: 'API-C',
        auth: 'tenant',
        body: UndoSchema,
        response: NotificationCountsSchema,
    }),
    'notifications.preferences': ep({
        method: 'GET',
        path: '/v1/notifications/preferences',
        summary: 'Channel matrix, quiet hours, digest',
        owner: 'API-C',
        auth: 'tenant',
        response: NotificationPreferencesSchema,
    }),
    'notifications.updatePreferences': ep({
        method: 'PATCH',
        path: '/v1/notifications/preferences',
        summary: 'Update preferences',
        owner: 'API-C',
        auth: 'tenant',
        body: NotificationPreferencesUpdateSchema,
        response: NotificationPreferencesSchema,
    }),
    'notifications.muteMission': ep({
        method: 'PUT',
        path: '/v1/notifications/mute-mission',
        summary: 'Mute / unmute a mission',
        owner: 'API-C',
        auth: 'tenant',
        body: MuteMissionSchema,
        response: OkSchema,
    }),
    'push.config': ep({
        method: 'GET',
        path: '/v1/push/config',
        summary: 'VAPID public key, tenant entitlement',
        owner: 'API-C',
        auth: 'tenant',
        response: PushConfigSchema,
    }),
    'push.subscribe': ep({
        method: 'POST',
        path: '/v1/push/subscriptions',
        summary: 'Register this device',
        owner: 'API-C',
        auth: 'tenant',
        entitlement: 'web_push',
        body: PushSubscribeSchema,
        response: PushDeviceSchema,
    }),
    'push.devices': ep({
        method: 'GET',
        path: '/v1/push/subscriptions',
        summary: 'My push devices',
        owner: 'API-C',
        auth: 'tenant',
        response: list(PushDeviceSchema),
    }),
    'push.unsubscribe': ep({
        method: 'DELETE',
        path: '/v1/push/subscriptions/:id',
        summary: 'Remove device',
        owner: 'API-C',
        auth: 'tenant',
        params: P.id,
        response: OkSchema,
    }),
    'stream.presence': ep({
        method: 'POST',
        path: '/v1/stream/presence',
        summary: 'Tab visibility ping for the open SSE stream (focused tabs suppress web push)',
        owner: 'API-C',
        auth: 'tenant',
        body: z.object({ focused: z.boolean() }),
        response: OkSchema,
    }),
    'stream.tenant': ep({
        method: 'GET',
        path: '/v1/stream',
        summary: 'SSE stream (see SSE_EVENTS)',
        owner: 'API-C',
        auth: 'tenant',
        query: z.object({ access_token: z.string().optional() }),
        response: StreamResponse,
        stream: true,
    }),
    'hq.notifications.list': ep({
        method: 'GET',
        path: '/v1/hq/notifications',
        summary: 'Mission HQ inbox',
        owner: 'API-C',
        auth: 'platform',
        query: PageQuerySchema.extend({ tab: z.enum(['all', 'unread']).default('all') }),
        response: pageOf(HqNotificationSchema),
        paginated: true,
    }),
    'hq.notifications.counts': ep({
        method: 'GET',
        path: '/v1/hq/notifications/counts',
        summary: 'HQ badge counts',
        owner: 'API-C',
        auth: 'platform',
        response: z.object({ unseen: z.number().int(), unread: z.number().int() }),
    }),
    'hq.notifications.seen': ep({
        method: 'POST',
        path: '/v1/hq/notifications/seen',
        summary: 'Batch mark seen',
        owner: 'API-C',
        auth: 'platform',
        body: NotificationIdsSchema,
        response: z.object({ unseen: z.number().int(), unread: z.number().int() }),
    }),
    'hq.notifications.read': ep({
        method: 'POST',
        path: '/v1/hq/notifications/:id/read',
        summary: 'Mark read',
        owner: 'API-C',
        auth: 'platform',
        params: P.id,
        response: HqNotificationSchema,
    }),
    'hq.notifications.readAll': ep({
        method: 'POST',
        path: '/v1/hq/notifications/read-all',
        summary: 'Mark all read',
        owner: 'API-C',
        auth: 'platform',
        response: ReadAllResultSchema,
    }),
    'hq.notifications.undoReadAll': ep({
        method: 'POST',
        path: '/v1/hq/notifications/read-all/undo',
        summary: 'Undo mark-all-read (Mission HQ inbox)',
        owner: 'API-C',
        auth: 'platform',
        body: UndoSchema,
        response: z.object({ unseen: z.number().int(), unread: z.number().int() }),
    }),
    'stream.hq': ep({
        method: 'GET',
        path: '/v1/hq/stream',
        summary: 'Mission HQ SSE (see HQ_SSE_EVENTS)',
        owner: 'API-C',
        auth: 'platform',
        query: z.object({ access_token: z.string().optional() }),
        response: StreamResponse,
        stream: true,
    }),
};
export const endpointKeys = Object.keys(endpoints);
export { None as NoInput };
//# sourceMappingURL=endpoints.js.map